import { useEffect, useState } from 'react';
import Sheet from '../app/components/Sheet';
import { useApp } from '../app/lib/AppContext';

const INPUT = 'input h-11 w-full rounded-[14px] border-line text-base';
const BUTTON = 'btn h-11 rounded-[14px] border-line bg-base-100 px-4';
const num = (v, digits = 1) => (v == null ? '–' : Number(v).toLocaleString('vi-VN', { maximumFractionDigits: digits }));

// "Thêm từ USDA": search USDA FoodData Central (in English) for a food the Vietnamese table lacks, pick the right
// entry (USDA's ranking is odd: check the name and the numbers), give it its Vietnamese name, group and everyday
// names, and add it. Later analyses use it; logged meals don't change. Opened from a "Cần xem lại" row (`flag`), the
// AI's hint aims at that name, the name is an everyday name of the food added, and the row is linked to it.
export default function UsdaSheet({ open, groups, flag, onClose, onAdded }) {
  const { api } = useApp();
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [recommended, setRecommended] = useState(null);
  const [picked, setPicked] = useState(null);
  const [form, setForm] = useState({ name_vi: '', group_code: '', aliases: '', cooked_factor: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (open) { setQ(''); setResults(null); setRecommended(null); setPicked(null); setError(null); } }, [open]);

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  // Typed words, or (opened from a "Cần xem lại" row, nothing typed) the AI's English words for the row's name.
  const find = (words) => run(async () => {
    setPicked(null);
    const params = new URLSearchParams();
    if (words) params.set('q', words);
    if (flag) params.set('for', flag.name);
    const res = await api.get(`/admin/usda_foods?${params}`);
    setQ(res.query);
    setResults(res.foods);
    setRecommended(res.recommended);
  });
  const search = (e) => {
    e.preventDefault();
    if (q.trim() || flag) find(q.trim());
  };
  // Opened from a row: search at once.
  useEffect(() => { if (open && flag) find(''); }, [open, flag?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (food) => { setPicked(food); setForm({ name_vi: '', group_code: '', aliases: flag?.name || '', cooked_factor: '' }); };
  const add = (e) => {
    e.preventDefault();
    run(async () => {
      const food = await api.post('/admin/usda_foods', {
        fdc_id: picked.fdc_id, flag_id: flag?.id, name_vi: form.name_vi, group_code: form.group_code, cooked_factor: form.cooked_factor,
        aliases: form.aliases.split(',').map((a) => a.trim()).filter(Boolean),
      });
      onAdded(food);
    });
  };
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const macros = (f) => `${num(f.kcal, 0)} kcal · Đ ${num(f.protein_g)} · B ${num(f.fat_g)} · TB ${num(f.carbs_g)}`;

  return (
    <Sheet open={open} onClose={onClose} title="Thêm từ USDA" badge={<span className="text-xs text-muted">Giá trị trên 100 g · tìm bằng tiếng Anh</span>}>
      <div className="flex flex-col gap-4">
        {!picked && (
          <>
            <form className="flex gap-2" onSubmit={search}>
              <input className={INPUT} placeholder="VD: chicken thigh skinless raw" value={q} onChange={(e) => setQ(e.target.value)} />
              <button type="submit" className={BUTTON} disabled={busy}>{busy ? <span className="loading loading-spinner loading-sm" /> : 'Tìm'}</button>
            </form>
            {flag && <p className="text-sm">Tìm cho <b>{flag.name}</b>: AI dịch sang tiếng Anh và đánh dấu kết quả hợp nhất. Sửa từ tìm rồi bấm Tìm nếu chưa đúng.</p>}
            <p className="text-xs text-muted">USDA xếp kết quả không theo độ khớp: đọc kỹ tên và số liệu, ưu tiên loại sống (raw), không da (skinless) nếu đúng món.</p>
            {results?.length === 0 && <p className="text-sm text-muted">Không tìm thấy. Thử từ khác bằng tiếng Anh.</p>}
            {results?.length > 0 && (
              <ul className="divide-y divide-line rounded-[14px] border border-line px-3">
                {results.map((f) => (
                  <li key={f.fdc_id} className="flex items-center gap-3 py-2.5">
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold">
                        {f.name_en}
                        {recommended?.fdc_id === f.fdc_id && <span className="ml-1.5 rounded-full bg-[#e6f4ea] px-2 py-0.5 text-[11px] font-bold text-[#1e7b3a]">Gợi ý</span>}
                      </span>
                      <span className="block text-xs text-muted">{macros(f)} · {f.data_type} · {f.fdc_id}</span>
                      {recommended?.fdc_id === f.fdc_id && recommended.reason && <span className="mt-0.5 block text-xs text-[#1e7b3a]">AI: {recommended.reason}</span>}
                    </span>
                    {f.imported
                      ? <span className="shrink-0 rounded-full bg-base-200 px-2.5 py-1 text-xs font-semibold text-muted">Đã có</span>
                      : <button type="button" className={`${BUTTON} h-10 shrink-0`} onClick={() => pick(f)}>Chọn</button>}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {picked && (
          <form className="flex flex-col gap-3" onSubmit={add}>
            <div className="rounded-[14px] bg-base-200 p-3 text-sm">
              <p className="font-semibold">{picked.name_en}</p>
              <p className="text-muted">{macros(picked)} · USDA {picked.fdc_id}</p>
            </div>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-semibold">Tên tiếng Việt</span>
              <input required className={INPUT} placeholder="VD: Đùi gà (bỏ da, sống)" value={form.name_vi} onChange={set('name_vi')} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-semibold">Nhóm</span>
              <select required className="select h-11 w-full rounded-[14px] border-line" value={form.group_code} onChange={set('group_code')}>
                <option value="">Chọn nhóm…</option>
                {groups.map((g) => <option key={g.code} value={g.code}>{g.name}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-semibold">Tên gọi khác (cách nhau bằng dấu phẩy)</span>
              <input className={INPUT} placeholder="VD: đùi gà bỏ da, má đùi gà" value={form.aliases} onChange={set('aliases')} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-semibold">Hệ số chín (không bắt buộc)</span>
              <input type="number" step="0.05" min="0.2" max="4" className={`${INPUT} w-32`} placeholder="Theo nhóm" value={form.cooked_factor} onChange={set('cooked_factor')} />
              <span className="text-xs text-muted">1 nếu ăn đúng khối lượng như khi cân (whey, sữa chua, hạt, đồ hộp).</span>
            </label>
            {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
            <div className="flex gap-2">
              <button type="submit" className="btn btn-primary h-11 flex-1 rounded-[14px] border-0" disabled={busy}>
                {busy && <span className="loading loading-spinner loading-sm" />} Thêm vào bảng
              </button>
              <button type="button" className={BUTTON} disabled={busy} onClick={() => setPicked(null)}>Chọn lại</button>
            </div>
          </form>
        )}
        {error && !picked && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
      </div>
    </Sheet>
  );
}
